import { environment } from '../../../environments/environment';
import { TEMPLATE_MAP } from '../../../environments/trackline/templates/template-registry';

// 'generic' is voor templates die niet aan een entityType/PlanningFormData hangen: de fieldKey
// wordt dan rechtstreeks opgezocht in de losse `fields`-map die aan FieldsTemplateComponent
// wordt meegegeven, in plaats van via de planning/phe-structuur.
export type FieldGroup = 'planning' | 'phe' | 'pheData' | 'planningData' | 'generic';

export interface EntityTypeFields {
    fields_planning?: Record<string, unknown> | null;
    fields_planning_data?: Record<string, unknown> | null;
    fields_phe?: Record<string, unknown> | null;
    fields_phe_data?: Record<string, unknown> | null;
}

// Maps a fieldGroup to the correct set of keys from the EntityTypeFields shape.
type FieldKeysForGroup<ET extends EntityTypeFields, G extends FieldGroup> = G extends 'planning'
    ? keyof NonNullable<ET['fields_planning']>
    : G extends 'planningData'
      ? keyof NonNullable<ET['fields_planning_data']>
      : G extends 'phe'
        ? keyof NonNullable<ET['fields_phe']>
        : G extends 'pheData'
          ? keyof NonNullable<ET['fields_phe_data']>
          : string;

// Discriminated union: each variant locks fieldKey to only valid keys for its fieldGroup.
export type TemplateFieldItem<ET extends EntityTypeFields = EntityTypeFields> = {
    [G in FieldGroup]: {
        type: 'field';
        fieldGroup: G;
        fieldKey: FieldKeysForGroup<ET, G>;
        classes?: string;
    };
}[FieldGroup];

export interface TemplateRow<ET extends EntityTypeFields = EntityTypeFields> {
    type: 'row';
    classes?: string;
    children: TemplateNode<ET>[];
}

export interface TemplateDiv<ET extends EntityTypeFields = EntityTypeFields> {
    type: 'div';
    classes?: string;
    children: TemplateNode<ET>[];
}

export type TemplateNode<ET extends EntityTypeFields = EntityTypeFields> =
    | TemplateRow<ET>
    | TemplateDiv<ET>
    | TemplateFieldItem<ET>;

export interface FieldTemplate<ET extends EntityTypeFields = EntityTypeFields> {
    rows: (TemplateRow<ET> | TemplateDiv<ET>)[];
}

export function getTemplateForEntityType(key: string): FieldTemplate {
    return environment.entitytypeTemplates[key] ?? TEMPLATE_MAP.base;
}

export function defineRows<ET extends EntityTypeFields>(extraRows: TemplateRow<ET>[]): TemplateRow<EntityTypeFields>[] {
    return [...(extraRows as unknown as TemplateRow<EntityTypeFields>[])];
}
